import { contains } from 'ramda';
import { ComponentType, SyntheticEvent, useState } from 'react';
import Autosuggest, {
  GetSuggestionValue,
  InputProps,
  OnSuggestionSelected,
  OnSuggestionsClearRequested,
  ShouldRenderSuggestions,
  SuggestionsFetchRequested,
} from 'react-autosuggest';
import { useInputValueListener } from '@bridebook/ui/src/utils/hooks';
import Box from '../../../../components/fela/Box';
import { colors } from '../../../../themes/variables';
import Label from '../label';
import Suggestion from './suggestion';
import componentStyles from './suggestion-field.style';

interface IProps<TSuggestion> {
  suggestions: TSuggestion[];
  getSuggestionValue: GetSuggestionValue<TSuggestion>;
  id?: string;
  label?: string | Object;
  value?: string;
  required?: boolean;
  onSuggestionSelected?: OnSuggestionSelected<TSuggestion>;
  shouldRenderSuggestions?: ShouldRenderSuggestions;
  placeholder?: string;
  onChange?: Autosuggest.InputProps<TSuggestion>['onChange'];
  onCustomClick?: (val: string) => any;
  iconColor?: string;
  icon?: Function;
  iconWidth?: number;
  disabled?: boolean;
  onClick?: (e: SyntheticEvent<any>) => any;
  onFocus?: (e: SyntheticEvent<any>) => any;
  onBlur?: InputProps<TSuggestion>['onBlur'];
  onSuggestionsFetchRequested?: SuggestionsFetchRequested;
  onSuggestionsClearRequested?: OnSuggestionsClearRequested;
  supplierSuggest?: boolean;
  customSuggestion?: ComponentType<{ suggestion: TSuggestion }>;
  showCustomSuggestion?: boolean;
  customSuggestLabel?: string;
  resetOnSuggestionSelected?: boolean;
}

export interface IBaseSuggestion {
  custom?: boolean;
  name?: string;
}

const SuggestionField = <TSuggestion extends IBaseSuggestion>({
  id,
  label,
  required,
  suggestions = [],
  getSuggestionValue,
  onSuggestionsFetchRequested,
  shouldRenderSuggestions,
  placeholder,
  disabled,
  onClick,
  onFocus,
  onBlur,
  supplierSuggest,
  customSuggestion,
  showCustomSuggestion = false,
  icon: Icon,
  iconWidth = 10,
  customSuggestLabel,
  onChange,
  onSuggestionSelected,
  onCustomClick,
  resetOnSuggestionSelected,
  onSuggestionsClearRequested,
  value,
}: IProps<TSuggestion>) => {
  const [stateValue, setStateValue] = useState(value || '');
  useInputValueListener(value, setStateValue);

  const _onChange: Autosuggest.InputProps<TSuggestion>['onChange'] = (e, params): void => {
    const { value } = e.target as HTMLInputElement;
    setStateValue(value || '');
    if (onChange) onChange(e, params);
  };

  const _onSuggestionSelected: Autosuggest.OnSuggestionSelected<TSuggestion> = (
    e,
    selected,
  ): void => {
    if (!selected.suggestion.custom) {
      const value = selected.suggestion.name;
      setStateValue(value);
      if (onSuggestionSelected) onSuggestionSelected(e, selected);
      if (resetOnSuggestionSelected) setStateValue('');
      return;
    }
    if (onCustomClick) onCustomClick(stateValue);
  };

  const _onSuggestionsClearRequested = (): void => {
    if (onSuggestionsClearRequested) onSuggestionsClearRequested();
  };

  const styles = componentStyles({
    disabled,
    icon: Boolean(Icon),
    iconWidth,
  });

  const inputProps: Autosuggest.InputProps<TSuggestion> = {
    placeholder: placeholder || '',
    value: stateValue,
    onChange: _onChange,
    id,
    disabled,
    onFocus,
    onBlur,
    onClick,
  };

  const renderSuggestion = (suggestion: TSuggestion) => (
    <Suggestion
      suggestion={suggestion}
      supplierSuggest={supplierSuggest}
      customSuggestion={customSuggestion}
      customSuggestLabel={customSuggestLabel}
    />
  );

  if (
    !contains({ custom: true } as TSuggestion, suggestions) &&
    (supplierSuggest || customSuggestion) &&
    showCustomSuggestion
  ) {
    const name = stateValue ? { name: stateValue } : {};
    suggestions.push({ ...name, custom: true } as TSuggestion);
  }

  return (
    <Box style={styles.wrapper}>
      <Label id={id} label={label} required={required} disabled={disabled} />
      <Box style={styles.inputWrapper}>
        {Icon && (
          <Box style={styles.iconWrap}>
            <Icon color={disabled ? colors.space30 : 'space'} width={iconWidth} />
          </Box>
        )}
        <Box style={styles.autosuggestWrap}>
          <Autosuggest<TSuggestion>
            onSuggestionSelected={_onSuggestionSelected}
            renderSuggestion={renderSuggestion}
            suggestions={suggestions}
            getSuggestionValue={getSuggestionValue}
            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
            inputProps={inputProps}
            shouldRenderSuggestions={shouldRenderSuggestions}
            onSuggestionsClearRequested={_onSuggestionsClearRequested}
            focusInputOnSuggestionClick={false}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SuggestionField;
